import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// Generate chart data
const generateChartData = (days, basePrice, volatility) => {
  const data = [];
  let price = basePrice;
  const now = new Date();
  
  for (let i = days; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const change = (Math.random() - 0.5) * volatility;
    price = Math.max(price * (1 + change), basePrice * 0.5);
    data.push({
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      price: price,
    });
  }
  return data;
};

const timeRanges = ['24H', '7D', '1M', '3M', '1Y'];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
        <p className="text-xs text-muted-foreground mb-1">{label}</p>
        <p className="text-base font-semibold font-mono">
          ${payload[0].value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </p>
      </div>
    );
  }
  return null;
};

export const PriceChart = ({ 
  coin = 'Bitcoin',
  symbol = 'BTC',
  currentPrice = 67234.56,
  priceChange = 2.34,
  className 
}) => {
  const [selectedRange, setSelectedRange] = useState('7D');
  const [chartData] = useState(() => generateChartData(30, currentPrice * 0.95, 0.03));
  
  const isPositive = priceChange >= 0;
  const minPrice = Math.min(...chartData.map(d => d.price));
  const maxPrice = Math.max(...chartData.map(d => d.price));

  return (
    <Card className={cn("", className)}>
      <CardHeader className="pb-2 px-4 sm:px-6">
        <div className="flex flex-col gap-4">
          {/* Coin Info */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-crypto-bitcoin/15 flex items-center justify-center shrink-0">
              <i className="fa-brands fa-bitcoin text-crypto-bitcoin text-lg"></i>
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base">{coin}</CardTitle>
                <Badge variant="secondary" className="font-mono text-xs">{symbol}</Badge>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xl font-semibold font-mono">
                  ${currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
                <Badge variant={isPositive ? 'gain' : 'loss'} className="text-xs">
                  {isPositive ? '+' : ''}{priceChange.toFixed(2)}%
                </Badge>
              </div>
            </div>
          </div>
          
          {/* Time Range Tabs */}
          <Tabs value={selectedRange} onValueChange={setSelectedRange}>
            <TabsList className="h-8 w-full sm:w-auto">
              {timeRanges.map((range) => (
                <TabsTrigger 
                  key={range} 
                  value={range}
                  className="text-xs px-3 h-7"
                >
                  {range}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>
      
      <CardContent className="px-2 sm:px-4 pb-4">
        <div className="h-[220px] sm:h-[280px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop 
                    offset="0%" 
                    stopColor={isPositive ? "hsl(var(--success))" : "hsl(var(--destructive))"} 
                    stopOpacity={0.2}
                  />
                  <stop 
                    offset="100%" 
                    stopColor={isPositive ? "hsl(var(--success))" : "hsl(var(--destructive))"} 
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="hsl(var(--border))" 
                vertical={false}
              />
              <XAxis 
                dataKey="date" 
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                dy={10}
                interval="preserveStartEnd"
              />
              <YAxis 
                domain={[minPrice * 0.98, maxPrice * 1.02]}
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                width={45}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="price"
                stroke={isPositive ? "hsl(var(--success))" : "hsl(var(--destructive))"}
                strokeWidth={2}
                fill="url(#priceGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        
        {/* Chart Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-border">
          <div>
            <p className="text-xs text-muted-foreground">24h High</p>
            <p className="font-mono font-medium text-sm text-success">${maxPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">24h Low</p>
            <p className="font-mono font-medium text-sm text-destructive">${minPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">24h Volume</p>
            <p className="font-mono font-medium text-sm">$28.4B</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Market Cap</p>
            <p className="font-mono font-medium text-sm">$1.32T</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PriceChart;
